import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SiteLogo } from "../components/SiteLogo";
import Starfield from "../components/landing/Starfield";
import api, { formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (password.length < 6) {
      setError("Şifre en az 6 karakter olmalı");
      return;
    }
    if (password !== confirm) {
      setError("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(res.data.message);
    } catch (err) {
      setError(formatApiError(err, "Şifre sıfırlanamadı"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="reset-password-page" className="relative min-h-screen bg-[#050505] flex items-center justify-center px-5 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0E17] to-[#050505]" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#0047FF]/10 blur-[130px]" />
      <Starfield count={50} />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex" data-testid="reset-logo">
            <SiteLogo size="lg" />
          </Link>
          <h1 className="font-display font-700 text-xl text-white mt-7" data-testid="reset-title">
            Yeni Şifre Belirle
          </h1>
        </div>

        <div className="rounded-3xl border border-white/[0.08] bg-[#0A0E17]/90 backdrop-blur p-8">
          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <p className="text-sm text-[#A0ABC0]">Geçersiz sıfırlama bağlantısı. Lütfen e-postanızdaki bağlantıyı kullanın.</p>
              <Link to="/giris" className="btn-glow inline-block mt-6 text-sm font-semibold text-white px-7 py-3 rounded-xl">
                Giriş Sayfasına Dön
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 size={44} strokeWidth={1.5} className="text-cyan-400 mx-auto mb-4" />
              <p className="text-sm text-[#A0ABC0]">Şifreniz başarıyla güncellendi. Yeni şifrenizle giriş yapabilirsiniz.</p>
              <Link to="/giris" data-testid="reset-login-link" className="btn-glow inline-block mt-6 text-sm font-semibold text-white px-7 py-3 rounded-xl">
                Giriş Yap
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Yeni Şifre</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      data-testid="reset-password-input"
                      className={`${inputCls} pr-11`}
                      placeholder="En az 6 karakter"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="reset-password-toggle"
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-cyan-300"
                      aria-label="Şifreyi göster"
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Yeni Şifre (Tekrar)</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    data-testid="reset-confirm-input"
                    className={inputCls}
                    placeholder="Şifrenizi tekrar girin"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <p data-testid="reset-error-message" className="mt-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="btn-glow mt-7 w-full text-sm font-semibold text-white py-3.5 rounded-xl disabled:opacity-50"
              >
                {loading ? "Güncelleniyor..." : "Şifremi Güncelle"}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" data-testid="reset-back-home" className="text-xs text-[#64748B] hover:text-cyan-300 transition-colors">
            ← Ana sayfaya dön
          </Link>
        </div>
      </div>
    </div>
  );
}
